import React, { memo } from 'react'
import orderBy from 'lodash/orderBy'
import { makeStyles } from 'transfix-ui/core/styles'
import { IStop } from 'types/FMSGraphqlTypes'
import ShipmentLaneDivider from './components/ShipmentLaneDivider'
import ShipmentStopDetails, { IShipmentStopDetailsProps } from './components/ShipmentStopDetails'
import ShipmentStopDetailsSkeleton from './components/ShipmentStopDetailsSkeleton'

const useShipmentLaneStyles = makeStyles(theme => ({
  flex: {
    display: 'flex',
    flexDirection: 'column',

    '& .stop': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  divider: {
    display: 'flex',
    margin: `-${theme.spacing(1.5)} 0 -${theme.spacing(1.5)} 3px`,

    '&.multiStop': {
      margin: `-${theme.spacing(1.5)} 0 -${theme.spacing(1.5)} -5px`,

      [theme.breakpoints.up('md')]: {
        margin: `0 ${theme.spacing(1.5)}`,
      },
    },

    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      alignItems: 'center',
      margin: `0 ${theme.spacing(1.5)}`,
    },
  },
}))
export interface IShipmentLaneProps {
  /**
   * @default false
   */
  loading?: boolean
  /**
   * @default []
   */
  stops?: IShipmentStopDetailsProps['stop'][]
  /**
   * @default 'shipment-lane'
   */
  dataTest?: string
  /**
   * @default false
   */
  disabled?: boolean
}

const ShipmentLane = memo<IShipmentLaneProps>(
  ({ disabled = false, loading = false, stops = [], dataTest = 'shipment-lane' }) => {
    const classes = useShipmentLaneStyles()

    const hasMultiStops = stops.length > 2

    const firstPickup = stops.find(
      ({ stopSequence, stopType }) => stopType === IStop.pickup && stopSequence === 1
    )

    const deliveries = stops.filter(({ stopType }) => stopType === IStop.dropoff)
    const sortedDeliveries = orderBy(deliveries, 'stopSequence', 'desc')
    const [lastDelivery] = sortedDeliveries || []

    return (
      <div className={classes.flex}>
        <div>
          {loading && <ShipmentStopDetailsSkeleton />}
          {!loading && firstPickup && (
            <ShipmentStopDetails
              stop={firstPickup}
              dataTest={`${dataTest}-firstPickup`}
              disabled={disabled}
            />
          )}
        </div>
        <div className={`${classes.divider} ${hasMultiStops ? 'multiStop' : ''}`}>
          <ShipmentLaneDivider stops={stops} loading={loading} dataTest={`${dataTest}-divider`} />
        </div>
        <div>
          {loading && <ShipmentStopDetailsSkeleton />}
          {!loading && lastDelivery && (
            <ShipmentStopDetails
              stop={lastDelivery}
              dataTest={`${dataTest}-lastDelivery`}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    )
  }
)

export default ShipmentLane
